/**
 * F2-c: sezione Anagrafica hotel (owner).
 *
 * Form curato (palette boutique, mobile-first): dati struttura + metodo
 * d'accesso (code/card/mixed/key) + feature flags. Salvataggio via PATCH
 * /owner/hotels/:tenantId con merge features JSONB lato server. Stato dirty,
 * feedback chiaro su salvataggio/errore, invalidate della query detail.
 */
import { Check, MapPin, Save, TriangleAlert } from '@hotel-manager/ui/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type AccessMethod,
  type HotelFeatures,
  type HotelPatch,
  type HotelTenant,
  patchHotel,
} from '../../../api/owner.js';

interface Props {
  tenantId: string;
  tenant: HotelTenant;
}

const ACCESS_METHODS: Array<{ value: AccessMethod; label: string; hint: string }> = [
  { value: 'code', label: 'Codice', hint: 'Tastierino numerico / serratura a codice' },
  { value: 'card', label: 'Card', hint: 'Tessera RFID (MIFARE/HID)' },
  { value: 'mixed', label: 'Misto', hint: 'Codice + card insieme' },
  { value: 'key', label: 'Chiave fisica', hint: 'Chiavi custodite al banco' },
];

const FEATURE_FLAGS: Array<{ key: keyof HotelFeatures; label: string; hint: string }> = [
  { key: 'enable_day_use', label: 'Day use', hint: 'Soggiorni a ore' },
  { key: 'enable_overnight', label: 'Pernottamento', hint: 'Notti intere' },
  { key: 'enable_room_service', label: 'Room service', hint: 'Servizio in camera' },
  { key: 'enable_alloggiati', label: 'Alloggiati', hint: 'Schedina Questura' },
  { key: 'enable_garage', label: 'Garage', hint: 'Posti auto' },
];

type FormState = {
  name: string;
  address: string;
  city: string;
  province: string;
  cap: string;
  phone: string;
  email: string;
  vatNumber: string;
  accessMethod: AccessMethod;
  features: HotelFeatures;
};

function toForm(t: HotelTenant): FormState {
  return {
    name: t.name ?? '',
    address: t.address ?? '',
    city: t.city ?? '',
    province: t.province ?? '',
    cap: t.cap ?? '',
    phone: t.phone ?? '',
    email: t.email ?? '',
    vatNumber: t.vatNumber ?? '',
    accessMethod: t.accessMethod,
    features: { ...t.featuresJson },
  };
}

export function AnagraficaSection({ tenantId, tenant }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => toForm(tenant));
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (patch: HotelPatch) => patchHotel(tenantId, patch),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['owner', 'hotel', tenantId] });
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  function toggleFeature(key: keyof HotelFeatures) {
    setForm((f) => ({ ...f, features: { ...f.features, [key]: !f.features[key] } }));
    setSaved(false);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Stringhe vuote -> null (campi nullable lato DB).
    const nn = (s: string) => (s.trim() === '' ? null : s.trim());
    mutation.mutate({
      name: form.name.trim(),
      address: nn(form.address),
      city: nn(form.city),
      province: nn(form.province),
      cap: nn(form.cap),
      phone: nn(form.phone),
      email: nn(form.email),
      vatNumber: nn(form.vatNumber),
      accessMethod: form.accessMethod,
      features: form.features,
    });
  }

  return (
    <form data-testid="anagrafica-section" onSubmit={onSubmit} className="flex flex-col gap-6">
      {/* Dati struttura */}
      <fieldset className="rounded-2xl border border-border-base bg-bg-card shadow-hm-sm p-5 sm:p-6">
        <legend className="px-1 text-sm font-bold text-text-primary flex items-center gap-1.5">
          <MapPin size={15} strokeWidth={1.75} className="text-accent-primary" />
          Dati struttura
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <Field label="Nome hotel" className="sm:col-span-2">
            <input
              data-testid="field-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
              maxLength={120}
              className={inputCls}
            />
          </Field>
          <Field label="Indirizzo" className="sm:col-span-2">
            <input
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              maxLength={256}
              className={inputCls}
            />
          </Field>
          <Field label="Città">
            <input
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              maxLength={120}
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Provincia">
              <input
                data-testid="field-province"
                value={form.province}
                onChange={(e) => set('province', e.target.value.toUpperCase().slice(0, 2))}
                maxLength={2}
                placeholder="MI"
                className={inputCls}
              />
            </Field>
            <Field label="CAP">
              <input
                value={form.cap}
                onChange={(e) => set('cap', e.target.value)}
                maxLength={10}
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Telefono">
            <input
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              maxLength={40}
              className={inputCls}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              maxLength={120}
              className={inputCls}
            />
          </Field>
          <Field label="Partita IVA">
            <input
              value={form.vatNumber}
              onChange={(e) => set('vatNumber', e.target.value)}
              maxLength={32}
              className={inputCls}
            />
          </Field>
        </div>
      </fieldset>

      {/* Metodo d'accesso */}
      <fieldset className="rounded-2xl border border-border-base bg-bg-card shadow-hm-sm p-5 sm:p-6">
        <legend className="px-1 text-sm font-bold text-text-primary">Metodo d'accesso</legend>
        <p className="text-xs text-text-secondary mt-1 mb-3">
          Come gli ospiti accedono alle camere di questo hotel.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ACCESS_METHODS.map((m) => {
            const selected = form.accessMethod === m.value;
            return (
              <button
                key={m.value}
                type="button"
                data-testid={`access-${m.value}`}
                onClick={() => set('accessMethod', m.value)}
                aria-pressed={selected}
                className={`text-left rounded-xl border p-3 transition-all ${
                  selected
                    ? 'border-accent-primary bg-accent-soft/50 ring-1 ring-accent-primary'
                    : 'border-border-base bg-bg-base hover:bg-bg-muted'
                }`}
              >
                <div className="text-sm font-semibold text-text-primary">{m.label}</div>
                <div className="text-[11px] text-text-muted leading-snug mt-0.5">{m.hint}</div>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Feature flags */}
      <fieldset className="rounded-2xl border border-border-base bg-bg-card shadow-hm-sm p-5 sm:p-6">
        <legend className="px-1 text-sm font-bold text-text-primary">Funzionalità attive</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          {FEATURE_FLAGS.map((f) => {
            const on = Boolean(form.features[f.key]);
            return (
              <button
                key={f.key}
                type="button"
                data-testid={`feature-${f.key}`}
                onClick={() => toggleFeature(f.key)}
                aria-pressed={on}
                className="flex items-center justify-between gap-3 rounded-xl border border-border-base bg-bg-base px-4 py-3 hover:bg-bg-muted transition-colors text-left"
              >
                <span>
                  <span className="block text-sm font-medium text-text-primary">{f.label}</span>
                  <span className="block text-[11px] text-text-muted">{f.hint}</span>
                </span>
                <span
                  aria-hidden="true"
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    on ? 'bg-accent-primary' : 'bg-border-base'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      on ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Footer azioni + feedback */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="submit"
          disabled={mutation.isPending}
          data-testid="anagrafica-save"
          className="inline-flex items-center gap-2 rounded-lg bg-accent-primary text-text-on-accent px-5 h-11 text-sm font-semibold hover:bg-accent-primary-hover transition-colors disabled:opacity-50"
        >
          <Save size={16} strokeWidth={1.75} />
          {mutation.isPending ? 'Salvataggio…' : 'Salva modifiche'}
        </button>
        {saved && (
          <output className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5F8A50]">
            <Check size={16} strokeWidth={2} /> Salvato
          </output>
        )}
        {mutation.isError && (
          <span
            role="alert"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#A8453A]"
          >
            <TriangleAlert size={16} strokeWidth={1.75} /> Errore nel salvataggio. Riprova.
          </span>
        )}
      </div>
    </form>
  );
}

const inputCls =
  'w-full rounded-lg border border-border-input bg-bg-card px-3 h-11 text-sm text-text-primary outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary transition-colors';

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: il control e' passato come children (associazione implicita).
    <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}
